import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  ArrowLeft,
  Calendar,
  CashCoin,
  Check,
  CreditCard,
  Pencil,
  Printer,
  Receipt,
  Trash,
} from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import RameStatusToggle from "../../components/RameStatusToggle";
import { useSchool } from "../../contexts/SchoolContext";
import { useAuth } from "../../hooks/useAuth";
import { secureApiEndpoints } from "../../utils/apiMigration";

const StudentPayment = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { schoolSettings, formatCurrency, getLogoUrl } = useSchool();
  const { user } = useAuth();

  const [student, setStudent] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [totals, setTotals] = useState({
    required: 0,
    paid: 0,
    remaining: 0,
    scholarship_amount: 0,
    has_scholarships: false,
  });

  const [discountInfo, setDiscountInfo] = useState({
    eligible_for_scholarship: false,
    scholarship_amount: 0,
    eligible_for_reduction: false,
    reduction_percentage: 0,
    deadline: null,
    reasons: [],
  });

  // Variable supprimée - logique intégrée dans le modal

  // Variables supprimées - ancienne logique de réduction

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptHtml, setReceiptHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isPrintingReceipt, setIsPrintingReceipt] = useState(false);
  const [printWindow, setPrintWindow] = useState(null);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);

  // États pour modifier/supprimer paiements (comptables)
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editForm, setEditForm] = useState({
    total_amount: '',
    payment_date: '',
    payment_method: '',
    reference_number: '',
    notes: '',
    payment_details: []
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: "",
    payment_date: new Date().toISOString().split("T")[0],
    versement_date: new Date().toISOString().split("T")[0],
    is_rame_physical: false,
    rame_choice: "none", // Par défaut: ne pas payer la RAME
    apply_discount: false, // Nouvelle option pour appliquer la réduction
    newcomer_discount_amount: "", // Réduction manuelle pour nouveaux élèves (T2/T3)
  });

  // États pour la gestion de la réduction dans le modal
  const [modalDiscountInfo, setModalDiscountInfo] = useState(null);
  const [isCheckingDiscount, setIsCheckingDiscount] = useState(false);
  const [newcomerDiscountAlreadyApplied, setNewcomerDiscountAlreadyApplied] = useState(false);
  const [appliedNewcomerDiscountAmount, setAppliedNewcomerDiscountAmount] = useState(0);

  // État pour la réduction employé (manuelle)
  const [employeeDiscount, setEmployeeDiscount] = useState(null);

  useEffect(() => {
    loadStudentPaymentInfo();
    loadPaymentHistory();
    loadEmployeeDiscount();
  }, [studentId]);

  const loadStudentPaymentInfo = async () => {
    try {
      setLoading(true);
      const response = await secureApiEndpoints.payments.getStudentInfo(
        studentId
      );

      console.log(response);
      
      if (response.success) {
        console.log(response);
        
        setStudent(response.data.student);
        setPaymentStatus(response.data.payment_status);
        setSchoolYear(response.data.school_year);

        // Calculer le total des réductions globales appliquées
        let totalGlobalDiscountAmount = 0;
        let hasGlobalDiscounts = false;

        if (
          response.data.payment_status &&
          Array.isArray(response.data.payment_status)
        ) {
          response.data.payment_status.forEach((status) => {
            if (
              status.has_global_discount &&
              status.global_discount_amount > 0
            ) {
              totalGlobalDiscountAmount += parseFloat(
                status.global_discount_amount
              );
              hasGlobalDiscounts = true;
            }
          });
        }

        // Gérer les montants (toujours normaux maintenant)
        const currentTotals = {
          required: response.data.total_required, // Montants normaux
          paid: response.data.total_paid,
          remaining: response.data.total_remaining,
          scholarship_amount: response.data.total_scholarship_amount || 0,
          has_scholarships: response.data.has_scholarships || false,
          global_discount_amount: totalGlobalDiscountAmount,
          has_global_discounts: hasGlobalDiscounts,
        };
        setTotals(currentTotals);

        // Code simplifié - plus besoin de tracking des réductions existantes

        setDiscountInfo({
          eligible_for_scholarship: false,
          scholarship_amount: 0,
          eligible_for_reduction: false,
          reduction_percentage: 0,
          deadline: null,
          reasons: [],
          ...(response.data.discount_info || {}),
        });

        // Plus besoin de vérifier l'éligibilité au chargement initial
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Erreur lors du chargement des informations de paiement");
      console.error("Error loading payment info:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      const response = await secureApiEndpoints.payments.getStudentHistory(
        studentId
      );

      if (response.success) {
        setPaymentHistory(response.data);

        // Détecter si une réduction newcomer a déjà été appliquée
        const newcomerPayment = response.data.find(payment =>
          payment.has_reduction &&
          payment.reduction_amount > 0 &&
          payment.discount_reason &&
          (payment.discount_reason.toLowerCase().includes('nouveau élève') ||
           payment.discount_reason.toLowerCase().includes('nouvel élève') ||
           payment.discount_reason.toLowerCase().includes('trimestre'))
        );

        if (newcomerPayment) {
          setNewcomerDiscountAlreadyApplied(true);
          setAppliedNewcomerDiscountAmount(newcomerPayment.reduction_amount);
        } else {
          setNewcomerDiscountAlreadyApplied(false);
          setAppliedNewcomerDiscountAmount(0);
        }
      }
    } catch (error) {
      console.error("Error loading payment history:", error);
    }
  };

  const loadEmployeeDiscount = async () => {
    try {
      const response = await secureApiEndpoints.manualDiscounts.getForStudent(studentId);
      if (response.success && response.data) {
        setEmployeeDiscount(response.data);
      } else {
        setEmployeeDiscount(null);
      }
    } catch (error) {
      console.error("Error loading employee discount:", error);
      setEmployeeDiscount(null);
    }
  };

  // Réinitialiser le formulaire quand le modal se ferme
  const handleModalClose = () => {
    setShowPaymentModal(false);
    setModalDiscountInfo(null);
    setPaymentForm((prev) => ({
      ...prev,
      amount: "",
      apply_discount: false,
      versement_date: new Date().toISOString().split("T")[0],
      newcomer_discount_amount: "", // Réinitialiser la réduction manuelle
    }));
  };

  // Fonction pour payer la RAME physiquement
  const handlePayRame = async () => {
    const result = await Swal.fire({
      title: "Payer la RAME physiquement",
      html: `
                <p>Confirmez-vous que l'étudiant <strong>${student?.first_name} ${student?.last_name}</strong> a apporté sa RAME physiquement ?</p>
                <div class="mt-3">
                    <label for="rameNotes" class="form-label">Notes (optionnel):</label>
                    <textarea id="rameNotes" class="form-control" placeholder="Commentaires sur le paiement RAME..."></textarea>
                </div>
            `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Oui, marquer comme payé",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      preConfirm: () => {
        const notes = document.getElementById("rameNotes").value;
        return { notes };
      },
    });

    if (result.isConfirmed) {
      try {
        setPaymentLoading(true);
        const response = await secureApiEndpoints.payments.payRamePhysically(
          studentId,
          {
            notes: result.value.notes || "RAME apportée physiquement",
            reference_number: "RAME-PHYS-" + new Date().getTime(),
          }
        );

        if (response.success) {
          Swal.fire({
            title: "Succès !",
            text: "RAME marquée comme payée physiquement",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });

          // Recharger les données
          await loadStudentPaymentInfo();
          await loadPaymentHistory();
        } else {
          throw new Error(response.message || "Erreur lors du paiement RAME");
        }
      } catch (error) {
        console.error("Error paying RAME:", error);
        Swal.fire({
          title: "Erreur",
          text: error.message || "Erreur lors du paiement RAME",
          icon: "error",
        });
      } finally {
        setPaymentLoading(false);
      }
    }
  };

  // Anciennes fonctions supprimées - logique intégrée dans le modal

  // Fonction pour paiement rapide sans modal
  const handleQuickPayment = async () => {
    // Calculer le montant avec bourse (conversion en nombres)
    const amountWithScholarship = Math.max(
      0,
      parseFloat(totals.remaining) - parseFloat(totals.scholarship_amount)
    );

    // Vérifier l'éligibilité aux réductions pour le paiement rapide
    let quickDiscountInfo = null;
    let amountWithDiscount = totals.remaining;

    try {
      // Utiliser la même logique que le modal normal
      const discountResponse =
        await secureApiEndpoints.payments.getStudentInfoWithDiscount(studentId);

      if (
        discountResponse.success &&
        discountResponse.data.discount_deadline &&
        !totals.has_scholarships
      ) {
        const deadlineDate = new Date(discountResponse.data.discount_deadline);
        const versementDate = new Date(); // Date actuelle pour paiement rapide

        if (versementDate <= deadlineDate) {
          // Calculer le montant avec réduction
          const totalRequired = discountResponse.data.total_required;
          const totalPaid = discountResponse.data.total_paid;
          const discountPercentage = discountResponse.data.discount_percentage;
          const discountAmount =
            (totalRequired - totalPaid) * (discountPercentage / 100);

          quickDiscountInfo = {
            discount_percentage: discountPercentage,
            discount_amount: discountAmount,
            deadline: discountResponse.data.discount_deadline,
            normal_totals: {
              total_required: totalRequired,
              total_paid: totalPaid,
            },
          };

          amountWithDiscount = totalRequired - totalPaid - discountAmount;
        }
      }
    } catch (error) {
      console.log(
        "Erreur lors de la vérification de l'éligibilité aux réductions pour paiement rapide:",
        error
      );
    }

    const result = await Swal.fire({
      title: "Paiement Rapide",
      html: `
                <div class="text-start">
                    <p><strong>Étudiant:</strong> ${student?.first_name} ${
        student?.last_name
      }</p>
                    <p><strong>Montant restant:</strong> ${formatAmount(
                      totals.remaining
                    )}</p>
                    ${
                      totals.has_scholarships
                        ? `
                        <div class="alert alert-success mb-3">
                            <strong>🎉 Avec votre bourse:</strong><br>
                            ${formatAmount(totals.remaining)} - ${formatAmount(
                            totals.scholarship_amount
                          )} = 
                            <strong>${formatAmount(
                              amountWithScholarship
                            )}</strong>
                        </div>
                    `
                        : quickDiscountInfo
                        ? `
                        <div class="alert alert-info mb-3">
                            <strong>💰 Réduction disponible (${
                              quickDiscountInfo.discount_percentage
                            }%):</strong><br>
                            ${formatAmount(totals.remaining)} - ${formatAmount(
                            quickDiscountInfo.discount_amount
                          )} = 
                            <strong>${formatAmount(amountWithDiscount)}</strong>
                            <br><small>Paiement intégral avant le ${new Date(
                              quickDiscountInfo.deadline
                            ).toLocaleDateString("fr-FR")}</small>
                        </div>
                    `
                        : ""
                    }
                    <hr>
                    <div class="mb-3">
                        <label for="quickAmount" class="form-label">Montant à payer *</label>
                        <input type="number" id="quickAmount" class="form-control" 
                               value="${
                                 totals.has_scholarships
                                   ? amountWithScholarship
                                   : quickDiscountInfo
                                   ? amountWithDiscount
                                   : totals.remaining
                               }" 
                               min="1" max="${totals.remaining}">
                    </div>
                    ${
                      quickDiscountInfo
                        ? `
                        <div class="mb-3">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="quickApplyDiscount" checked>
                                <label class="form-check-label" for="quickApplyDiscount">
                                    Appliquer la réduction de ${quickDiscountInfo.discount_percentage}%
                                </label>
                            </div>
                        </div>
                    `
                        : ""
                    }
                    <div class="mb-3">
                        <label for="quickVersementDate" class="form-label">Date de versement *</label>
                        <input type="date" id="quickVersementDate" class="form-control" 
                               value="${
                                 new Date().toISOString().split("T")[0]
                               }" required>
                    </div>
                    <div class="mb-3">
                        <label for="quickMethod" class="form-label">Méthode de paiement *</label>
                        <select id="quickMethod" class="form-select">
                            <option value="cash">Banque</option>
                            <option value="card">Carte bancaire</option>
                            <option value="transfer">Virement</option>
                            <option value="check">Chèque</option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label for="quickReference" class="form-label">Référence (optionnel)</label>
                        <input type="text" id="quickReference" class="form-control" 
                               placeholder="Numéro de référence">
                    </div>
                    <div class="mb-3">
                        <label for="quickNotes" class="form-label">Notes (optionnel)</label>
                        <textarea id="quickNotes" class="form-control" rows="2" 
                                  placeholder="Commentaires sur le paiement"></textarea>
                    </div>
                </div>
            `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "💳 Enregistrer le Paiement",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      width: "500px",
      didOpen: () => {
        // Gestionnaire pour la checkbox de réduction
        const discountCheckbox = document.getElementById("quickApplyDiscount");
        const amountInput = document.getElementById("quickAmount");

        if (discountCheckbox && amountInput && quickDiscountInfo) {
          discountCheckbox.addEventListener("change", () => {
            if (discountCheckbox.checked) {
              amountInput.value = amountWithDiscount;
            } else {
              amountInput.value = totals.remaining;
            }
          });
        }
      },
      preConfirm: () => {
        const amount = parseFloat(document.getElementById("quickAmount").value);
        const versementDate =
          document.getElementById("quickVersementDate").value;
        const method = document.getElementById("quickMethod").value;
        const reference = document
          .getElementById("quickReference")
          .value.trim();
        const notes = document.getElementById("quickNotes").value.trim();
        const applyDiscountCheckbox =
          document.getElementById("quickApplyDiscount");
        const applyDiscount = applyDiscountCheckbox
          ? applyDiscountCheckbox.checked
          : false;

        if (!amount || amount <= 0) {
          Swal.showValidationMessage("Veuillez saisir un montant valide");
          return false;
        }

        if (amount > totals.remaining) {
          Swal.showValidationMessage(
            "Le montant ne peut pas dépasser le montant restant"
          );
          return false;
        }

        if (!versementDate) {
          Swal.showValidationMessage(
            "Veuillez sélectionner une date de versement"
          );
          return false;
        }

        return {
          amount: amount,
          payment_method: method,
          reference_number: reference || "",
          notes: notes || "",
          payment_date: new Date().toISOString().split("T")[0],
          versement_date: versementDate,
          apply_global_discount: applyDiscount,
        };
      },
    });

    if (result.isConfirmed) {
      try {
        setPaymentLoading(true);
        const response = await secureApiEndpoints.payments.create({
          student_id: parseInt(studentId),
          ...result.value,
        });

        if (response.success) {
          Swal.fire({
            title: "Paiement Enregistré !",
            text: `Paiement de ${formatAmount(
              result.value.amount
            )} enregistré avec succès`,
            icon: "success",
            timer: 3000,
            showConfirmButton: false,
          });

          // Recharger les données
          await loadStudentPaymentInfo();
          await loadPaymentHistory();
        } else {
          throw new Error(
            response.message || "Erreur lors de l'enregistrement du paiement"
          );
        }
      } catch (error) {
        console.error("Error in quick payment:", error);
        Swal.fire({
          title: "Erreur",
          text: error.message || "Erreur lors de l'enregistrement du paiement",
          icon: "error",
        });
      } finally {
        setPaymentLoading(false);
      }
    }
  };

  // Fonction supprimée - logique maintenant intégrée dans le modal principal

  // Fonction supprimée - logique intégrée dans le modal

  // Fonction pour vérifier l'éligibilité aux réductions dans le modal
  const checkDiscountEligibilityInModal = async (versementDate) => {
    if (!studentId || !versementDate) {
      setModalDiscountInfo(null);
      return;
    }

    setIsCheckingDiscount(true);

    try {
      const response =
        await secureApiEndpoints.payments.getStudentInfoWithDiscount(studentId);

      if (response.success) {
        console.log("Discount response data:", response.data);

        // Vérifier si une date limite existe
        if (!response.data.discount_deadline) {
          console.log("No discount deadline found - should be eligible");
          setModalDiscountInfo(response.data);
          return;
        }

        // Vérifier si la date de versement est dans les délais
        const selectedDate = new Date(versementDate);

        // Parser la date française DD/MM/YYYY vers le format ISO
        const parseFrenchDate = (dateStr) => {
          const parts = dateStr.split("/");
          if (parts.length === 3) {
            // Convertir DD/MM/YYYY vers YYYY-MM-DD
            return new Date(
              `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(
                2,
                "0"
              )}`
            );
          }
          return new Date(dateStr); // Fallback
        };

        const deadline = parseFrenchDate(response.data.discount_deadline);

        // Vérifier que les dates sont valides
        if (isNaN(selectedDate.getTime()) || isNaN(deadline.getTime())) {
          console.error("Invalid dates:", {
            versementDate,
            discount_deadline: response.data.discount_deadline,
            selectedDateValid: !isNaN(selectedDate.getTime()),
            deadlineValid: !isNaN(deadline.getTime()),
          });
          setModalDiscountInfo({
            not_eligible: true,
            message: "Erreur de format de date",
          });
          return;
        }

        console.log("Date comparison:", {
          selectedDate: selectedDate.toDateString(),
          deadline: deadline.toDateString(),
          versementDate: versementDate,
          discount_deadline: response.data.discount_deadline,
          isValid: selectedDate <= deadline,
        });

        if (selectedDate <= deadline) {
          setModalDiscountInfo(response.data);
        } else {
          setModalDiscountInfo({
            ...response.data,
            is_date_expired: true,
            deadline_message: `Date limite dépassée (${response.data.discount_deadline})`,
          });
        }
      } else {
        // Étudiant non éligible aux réductions
        setModalDiscountInfo({
          not_eligible: true,
          message: response.message,
          reasons: response.reasons || [],
        });
      }
    } catch (error) {
      console.error("Error checking discount eligibility in modal:", error);
      setModalDiscountInfo(null);
    } finally {
      setIsCheckingDiscount(false);
    }
  };

  // Fonction pour gérer l'activation/désactivation de la réduction dans le modal
  const handleDiscountToggle = (isChecked) => {
    setPaymentForm((prev) => {
      const newForm = { ...prev, apply_discount: isChecked };

      if (
        isChecked &&
        modalDiscountInfo &&
        !modalDiscountInfo.not_eligible &&
        !modalDiscountInfo.is_date_expired
      ) {
        // Appliquer le montant avec réduction
        newForm.amount = modalDiscountInfo.payment_amount_required.toString();
      } else {
        // Revenir au montant par défaut (reste à payer)
        newForm.amount = totals.remaining.toString();
      }

      return newForm;
    });
  };

  // Fonction pour gérer les changements de montant
  const handleAmountChange = (newAmount) => {
    setPaymentForm((prev) => ({ ...prev, amount: newAmount }));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      setError("Veuillez saisir un montant valide");
      return;
    }

    if (parseFloat(paymentForm.amount) > totals.remaining) {
      setError(
        `Le montant saisi (${formatCurrency(
          parseInt(paymentForm.amount)
        )}) est supérieur au montant restant (${formatCurrency(
          totals.remaining
        )}). Veuillez saisir un montant inférieur ou égal au solde restant.`
      );
      return;
    }

    // NOUVELLE VALIDATION : Vérifier la cohérence entre la case cochée et le montant
    if (
      paymentForm.apply_discount &&
      modalDiscountInfo &&
      !modalDiscountInfo.not_eligible &&
      !modalDiscountInfo.is_date_expired
    ) {
      const expectedAmount = modalDiscountInfo.payment_amount_required;
      const paymentAmount = parseFloat(paymentForm.amount);

      if (Math.abs(paymentAmount - expectedAmount) > 0.01) {
        Swal.fire({
          title: "Montant incorrect pour la réduction",
          html: `
                        <div class="text-left">
                            <p>Pour appliquer la réduction, le montant doit être exactement :</p>
                            <p><strong>${formatAmount(
                              expectedAmount
                            )}</strong></p>
                            <p>Montant actuel : <strong>${formatAmount(
                              paymentAmount
                            )}</strong></p>
                        </div>
                    `,
          icon: "warning",
        });
        return;
      }
    }

    // Validation si réduction cochée mais conditions non remplies
    if (
      paymentForm.apply_discount &&
      (!modalDiscountInfo ||
        modalDiscountInfo.not_eligible ||
        modalDiscountInfo.is_date_expired)
    ) {
      Swal.fire({
        title: "Réduction non disponible",
        text: "Les conditions pour la réduction ne sont pas remplies. Veuillez décocher l'option ou modifier la date de versement.",
        icon: "warning",
      });
      return;
    }

    try {
      setPaymentLoading(true);
      setError("");

      const paymentData = {
        student_id: parseInt(studentId),
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null,
        notes: paymentForm.notes || null,
        payment_date: paymentForm.payment_date,
        versement_date: paymentForm.versement_date,
        apply_global_discount: paymentForm.apply_discount, // Indiquer au backend d'appliquer la réduction
      };

      // Ajouter la réduction manuelle pour nouveaux élèves (T2/T3)
      if (student?.student_status === 'new' && student?.arrival_trimester && paymentForm.newcomer_discount_amount) {
        paymentData.newcomer_discount_amount = parseFloat(paymentForm.newcomer_discount_amount);
      }

      const response = await secureApiEndpoints.payments.create(paymentData);

      if (response.success) {
        setSuccess("Paiement enregistré avec succès");
        setShowPaymentModal(false);

        // Réinitialiser le formulaire
        setPaymentForm({
          amount: "",
          payment_method: "cash",
          reference_number: "",
          notes: "",
          payment_date: new Date().toISOString().split("T")[0],
          versement_date: new Date().toISOString().split("T")[0],
          is_rame_physical: false,
          rame_choice: "none",
          apply_discount: false,
          newcomer_discount_amount: "", // Réinitialiser la réduction manuelle
        });
        setModalDiscountInfo(null);

        // Recharger les données
        await loadStudentPaymentInfo();
        await loadPaymentHistory();

        // Proposer d'imprimer le reçu
        const printResult = await Swal.fire({
          title: "Paiement enregistré !",
          text: "Voulez-vous imprimer le reçu maintenant ?",
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Imprimer le reçu",
          cancelButtonText: "Plus tard",
        });

        if (printResult.isConfirmed) {
          handlePrintReceipt(response.data.id);
        }
      } else {
        setError(
          response.message || "Erreur lors de l'enregistrement du paiement"
        );
      }
    } catch (error) {
      setError("Erreur lors de l'enregistrement du paiement");
      console.error("Error creating payment:", error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePrintReceipt = async (paymentId) => {
    if (isPrintingReceipt) return; // Éviter les appels multiples
    
    try {
      setIsPrintingReceipt(true);
      const response = await secureApiEndpoints.payments.generateReceipt(
        paymentId
      );

      if (response.success) {
        setReceiptHtml(response.data.html);
        setCurrentPaymentId(paymentId);
        setShowReceiptModal(true);
      } else {
        setError("Erreur lors de la génération du reçu");
      }
    } catch (error) {
      setError("Erreur lors de la génération du reçu");
      console.error("Error generating receipt:", error);
    } finally {
      setIsPrintingReceipt(false);
    }
  };

  const handlePrintReceiptFromHistory = async (paymentId) => {
    await handlePrintReceipt(paymentId);
  };

  const handleDownloadPDFFromHistory = async (paymentId) => {
    if (isPrintingReceipt) return;

    try {
      setIsPrintingReceipt(true);
      await secureApiEndpoints.payments.downloadReceiptPDF(paymentId);
      setSuccess("Le reçu PDF a été téléchargé avec succès");
    } catch (error) {
      console.error('Erreur lors du téléchargement PDF:', error);
      setError('Erreur lors du téléchargement du PDF: ' + error.message);
    } finally {
      setIsPrintingReceipt(false);
    }
  };

  const downloadReceiptPDF = async () => {
    if (isPrintingReceipt) return;

    try {
      setIsPrintingReceipt(true);
      
      if (!currentPaymentId) {
        setError("Impossible de déterminer l'ID du paiement");
        return;
      }

      await secureApiEndpoints.payments.downloadReceiptPDF(currentPaymentId);
      setSuccess("Le reçu PDF a été téléchargé avec succès");
      
    } catch (error) {
      console.error('Erreur lors du téléchargement PDF:', error);
      setError('Erreur lors du téléchargement du PDF: ' + error.message);
    } finally {
      setIsPrintingReceipt(false);
    }
  };

  const printReceipt = () => {
    if (isPrintingReceipt) return; // Éviter les doubles ouvertures
    
    // Fermer la fenêtre précédente si elle existe
    if (printWindow && !printWindow.closed) {
      printWindow.close();
    }
    
    setIsPrintingReceipt(true);
    const newPrintWindow = window.open("", "_blank");
    
    // Vérifier si la fenêtre a pu être créée (popups non bloqués)
    if (!newPrintWindow) {
      setIsPrintingReceipt(false);
      setError("Impossible d'ouvrir la fenêtre d'impression. Veuillez autoriser les popups pour ce site.");
      return;
    }
    
    setPrintWindow(newPrintWindow);
    // Extraire le numéro de reçu depuis le HTML pour un titre unique
    const receiptNumberMatch = receiptHtml.match(/Reçu N° :\s*([^<]+)/);
    const receiptNumber = receiptNumberMatch
      ? receiptNumberMatch[1].trim()
      : new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[-:]/g, "")
          .replace("T", "_");

    try {
      newPrintWindow.document.write(`
            <html>
                <head>
                    <title>Reçu_${receiptNumber}</title>
                    <style>
                        @page {
                            size: A4 landscape;
                            margin: 1cm;
                        }
                        
                        body { 
                            font-family: Arial, sans-serif; 
                            margin: 0; 
                            padding: 0;
                            font-size: 9px;
                            line-height: 1.3;
                        }
                        
                        @media print { 
                            body { 
                                margin: 0; 
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            .no-print { 
                                display: none !important; 
                            }
                        }
                        
                        @media screen {
                            body {
                                background: #f0f0f0;
                                padding: 20px;
                            }
                            
                            .receipt-container {
                                background: white;
                                width: 294mm;  /* Deux reçus côte à côte (147mm x 2) */
                                height: 148mm; /* Hauteur à 148mm */
                                margin: 0 auto;
                                box-shadow: 0 0 10px rgba(0,0,0,0.1);
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-container">
                        ${receiptHtml}
                    </div>
                    <div class="no-print" style="text-align: center; margin-top: 30px; background: white; padding: 20px;">
                        <button onclick="window.print(); setTimeout(() => window.close(), 2000);" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer; margin-right: 10px;">📄 Imprimer & Fermer</button>
                        <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer;">✖️ Fermer Maintenant</button>
                        <div style="margin-top: 15px; font-size: 12px; color: #666;">
                            <p>📋 <strong>Instructions :</strong></p>
                            <ul style="text-align: left; max-width: 500px; margin: 0 auto;">
                                <li>Configurez votre imprimante sur format <strong>A4 Paysage</strong></li>
                                <li>Le reçu contient 2 exemplaires côte à côte</li>
                                <li><strong>Côté gauche :</strong> Exemplaire parents</li>
                                <li><strong>Côté droit :</strong> Exemplaire collège</li>
                                <li>Découpez au milieu vertical pour séparer les exemplaires</li>
                            </ul>
                        </div>
                    </div>
                </body>
            </html>
        `);
      newPrintWindow.document.close();
      
      // Libérer le verrou après un délai pour permettre l'ouverture de la fenêtre
      setTimeout(() => {
        setIsPrintingReceipt(false);
      }, 1000);
      
      // Écouter la fermeture de la fenêtre pour libérer le verrou plus tôt si possible
      if (newPrintWindow) {
        const checkClosed = setInterval(() => {
          if (newPrintWindow.closed) {
            setIsPrintingReceipt(false);
            setPrintWindow(null);
            clearInterval(checkClosed);
          }
        }, 500);
        
        // Nettoyer l'intervalle après 30 secondes au maximum
        setTimeout(() => {
          clearInterval(checkClosed);
          setIsPrintingReceipt(false);
          setPrintWindow(null);
        }, 30000);
      }
    } catch (error) {
      console.error('Erreur lors de l\'ouverture de la fenêtre d\'impression:', error);
      setError('Erreur lors de l\'ouverture de la fenêtre d\'impression. Veuillez réessayer.');
      setIsPrintingReceipt(false);
      setPrintWindow(null);
    }
  };


  const formatAmount = (amount) => {
    return formatCurrency(parseInt(amount));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  };

  const getPaymentMethodLabel = (method, isRamePhysical = false) => {
    if (isRamePhysical) {
      return "RAME Physique";
    }
    const methods = {
      cash: "Banque",
      card: "Carte",
      transfer: "Virement",
      check: "Chèque",
    };
    return methods[method] || method;
  };

  // Fonctions pour modifier/supprimer paiements (comptables)
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);

    // Utiliser les payment_details existants ou créer un détail par défaut
    let paymentDetails = [];

    if (payment.payment_details && payment.payment_details.length > 0) {
      // Transformer les payment_details existants au format attendu par le backend
      paymentDetails = payment.payment_details.map(detail => ({
        payment_tranche_id: detail.payment_tranche_id || detail.payment_tranche?.id,
        amount: detail.amount_allocated || detail.amount || detail.new_total_amount
      }));
    } else {
      // Utiliser la première tranche disponible dans paymentStatus
      const availableTranches = paymentStatus.filter(status => status.tranche);

      if (availableTranches.length > 0) {
        const firstTranche = availableTranches[0];
        paymentDetails = [
          {
            payment_tranche_id: firstTranche.tranche.id,
            amount: payment.total_amount
          }
        ];
      } else {
        // Fallback: utiliser l'ID 1 par défaut
        paymentDetails = [
          {
            payment_tranche_id: 1,
            amount: payment.total_amount
          }
        ];
      }
    }

    setEditForm({
      total_amount: payment.total_amount,
      payment_date: payment.payment_date ? payment.payment_date.split('T')[0] : '',
      payment_method: payment.payment_method,
      reference_number: payment.reference_number || '',
      notes: payment.notes || '',
      payment_details: paymentDetails
    });
    setShowEditPaymentModal(true);
  };

  const handleSaveEditPayment = async () => {
    try {
      // Le backend gère maintenant automatiquement la répartition des paiements
      // Plus besoin d'envoyer payment_details
      const dataToSend = {
        total_amount: editForm.total_amount,
        payment_date: editForm.payment_date,
        payment_method: editForm.payment_method,
        reference_number: editForm.reference_number,
        notes: editForm.notes
      };


      const response = await secureApiEndpoints.payments.update(editingPayment.id, dataToSend);
      if (response.success) {
        setShowEditPaymentModal(false);
        setSuccess('Paiement modifié avec succès - Actualisation en cours...');

        // Recharger complètement les données pour s'assurer que tous les calculs sont à jour
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setError(response.message || 'Erreur lors de la modification');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      console.error('Réponse de l\'erreur:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la modification du paiement';
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        console.error('Erreurs de validation:', validationErrors);
      }
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeletePayment = async (payment) => {
    const result = await Swal.fire({
      title: 'Supprimer le paiement',
      html: `
        <p>Êtes-vous sûr de vouloir supprimer ce paiement ?</p>
        <div class="mt-3">
          <strong>Montant :</strong> ${formatAmount(payment.total_amount)}<br>
          <strong>Date :</strong> ${formatDate(payment.payment_date)}<br>
          <strong>Reçu N° :</strong> ${payment.receipt_number}
        </div>
        <div class="mt-3">
          <label for="deletion-reason" class="form-label">Raison de la suppression :</label>
          <textarea id="deletion-reason" class="form-control" placeholder="Expliquez pourquoi vous supprimez ce paiement..." rows="3"></textarea>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      preConfirm: () => {
        const reason = document.getElementById('deletion-reason').value;
        if (!reason.trim()) {
          Swal.showValidationMessage('Veuillez indiquer une raison pour la suppression');
          return false;
        }
        return reason;
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await secureApiEndpoints.payments.cancel(payment.id, {
          cancellation_reason: result.value
        });
        if (response.success) {
          await loadPaymentHistory();
          await loadStudentPaymentInfo();
          setSuccess('Paiement supprimé avec succès');
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.message || 'Erreur lors de la suppression');
          setTimeout(() => setError(''), 3000);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        setError('Erreur lors de la suppression du paiement');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Fonction pour supprimer tout l'historique des paiements (comptable supérieur uniquement)
  const handleDeleteHistory = async () => {
    const result = await Swal.fire({
      title: 'Supprimer tout l\'historique',
      html: `
        <p><strong>⚠️ Attention : Cette action est irréversible !</strong></p>
        <p>Êtes-vous sûr de vouloir supprimer <strong>TOUT</strong> l'historique des paiements de cet étudiant ?</p>
        <div class="mt-3">
          <strong>Étudiant :</strong> ${student?.first_name} ${student?.last_name}<br>
          <strong>Nombre de paiements :</strong> ${paymentHistory.length}
        </div>
        <div class="mt-3">
          <label for="deletion-reason" class="form-label">Raison de la suppression de l'historique :</label>
          <textarea id="deletion-reason" class="form-control" placeholder="Expliquez pourquoi vous supprimez tout l'historique..." rows="3"></textarea>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Supprimer tout',
      cancelButtonText: 'Annuler',
      preConfirm: () => {
        const reason = document.getElementById('deletion-reason').value;
        if (!reason.trim()) {
          Swal.showValidationMessage('Veuillez indiquer une raison pour la suppression');
          return false;
        }
        return reason;
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await secureApiEndpoints.payments.deleteHistory(student.id);
        if (response.success) {
          setPaymentHistory([]);
          await loadStudentPaymentInfo();
          setSuccess('Historique des paiements supprimé avec succès');
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(response.message || 'Erreur lors de la suppression de l\'historique');
          setTimeout(() => setError(''), 3000);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'historique:', error);
        setError('Erreur lors de la suppression de l\'historique des paiements');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!student) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          Étudiant non trouvé ou erreur lors du chargement des données.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(-1)}
                className="me-3"
              >
                <ArrowLeft size={16} />
              </Button>
              <div>
                <h2 className="mb-1">
                  Paiement - {student.last_name} {student.first_name}
                </h2>
                <p className="text-muted mb-0">
                  {student.classSeries?.schoolClass?.name} -{" "}
                  {student.classSeries?.name} | {schoolYear?.name}
                </p>
              </div>
            </div>
            {totals.remaining <= 0 ? (
              <Button variant="success" disabled>
                <CashCoin size={16} className="me-2" />
                Paiements Complets
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  setShowPaymentModal(true);
                  // Réinitialiser les états du modal
                  setModalDiscountInfo(null);
                  setPaymentForm((prev) => ({
                    ...prev,
                    amount: "",
                    apply_discount: false,
                    versement_date: new Date().toISOString().split("T")[0],
                    newcomer_discount_amount: "", // Réinitialiser la réduction manuelle
                  }));
                }}
              >
                <CashCoin size={16} className="me-2" />
                Nouveau Paiement
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Réduction "nouveau élève" (T2/T3) - Affichage selon statut */}
      {student?.student_status === 'new' && student?.arrival_trimester && (
        newcomerDiscountAlreadyApplied ? (
          // Réduction déjà appliquée - Message informatif
          <Alert variant="success" className="mb-3">
            <strong>✅ Réduction scolarité (nouveau élève) - Déjà appliquée</strong>
            <div className="mt-2">
              <div>
                Arrivée : <strong>{student.arrival_trimester}e trimestre</strong>
              </div>
              {student.newcomer_discount_reason && (
                <div className="mt-1">
                  <small>
                    <em>Motif : {student.newcomer_discount_reason}</em>
                  </small>
                </div>
              )}
              <div className="mt-2">
                <strong>Réduction appliquée :</strong> {formatAmount(appliedNewcomerDiscountAmount)} FCFA
              </div>
              <small className="text-muted d-block mt-1">
                La réduction a été distribuée sur les tranches de scolarité. Vous pouvez consulter le détail dans le statut ci-dessous.
              </small>
            </div>
          </Alert>
        ) : (
          // Réduction pas encore appliquée - Demande de saisie manuelle
          <Alert variant="warning" className="mb-3">
            <strong>⚠️ Réduction scolarité (nouveau élève) - SAISIE MANUELLE REQUISE</strong>
            <div>
              Arrivée : <strong>{student.arrival_trimester}e trimestre</strong>
            </div>
            {student.newcomer_discount_reason && (
              <div className="mt-1">
                <small>
                  <em>Motif : {student.newcomer_discount_reason}</em>
                </small>
              </div>
            )}
            <div className="mt-2 p-2" style={{backgroundColor: '#fff3cd', borderRadius: '5px', border: '1px solid #ffc107'}}>
              <strong>⚡ Action requise :</strong> Vous devez saisir <strong>MANUELLEMENT</strong> le montant de réduction dans le champ ci-dessous.
              <br />
              <small className="text-muted">La réduction ne sera PAS calculée automatiquement.</small>
            </div>
          </Alert>
        )
      )}

      {/* Alerte Réduction Employé */}
      {employeeDiscount && employeeDiscount.amount > 0 && (
        <Alert variant="info" className="mb-3">
          <strong>💼 Réduction Employé Appliquée</strong>
          <div className="mt-2">
            <div>
              <strong>Montant :</strong> {formatAmount(employeeDiscount.amount)} FCFA
            </div>
            {employeeDiscount.reason && (
              <div className="mt-1">
                <strong>Motif :</strong> <em>{employeeDiscount.reason}</em>
              </div>
            )}
          </div>
          <div className="mt-2">
            <small className="text-muted">
              Cette réduction est appliquée automatiquement aux frais de scolarité (hors inscription) et apparaît dans le statut de paiement ci-dessous.
            </small>
          </div>
        </Alert>
      )}

      {/* Interface épurée - pas d'info sur les réductions au chargement initial */}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{formatAmount(totals.required)}</h3>
              <p className="text-muted mb-0">Total à payer</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{formatAmount(totals.paid)}</h3>
              <p className="text-muted mb-0">Total payé</p>
              {totals.has_global_discounts &&
                totals.global_discount_amount > 0 && (
                  <small className="text-info d-block mt-1">
                    {formatAmount(totals.paid)} +{" "}
                    {formatAmount(totals.global_discount_amount)} ={" "}
                    {formatAmount(totals.required)}
                  </small>
                )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              {totals.has_scholarships ? (
                <>
                  <h3
                    className={
                      totals.remaining > 0 ? "text-warning" : "text-success"
                    }
                  >
                    {formatAmount(totals.remaining)}
                  </h3>
                  <p className="text-muted mb-0">Reste à payer</p>
                  <small className="text-success">
                    (Avec bourse de {formatAmount(totals.scholarship_amount)}{" "}
                    déjà appliquée)
                  </small>
                </>
              ) : (
                <>
                  <h3
                    className={
                      totals.remaining > 0 ? "text-warning" : "text-success"
                    }
                  >
                    {formatAmount(totals.remaining)}
                  </h3>
                  <p className="text-muted mb-0">Reste à payer</p>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Information sur la bourse (si applicable) */}
      {totals.has_scholarships && totals.remaining > 0 && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" className="text-center">
              <i className="bi bi-award me-2"></i>
              <strong>Bonne nouvelle !</strong> Vous bénéficiez d'une bourse de{" "}
              {formatAmount(totals.scholarship_amount)}.
              <br />
              <small>
                Cette réduction sera automatiquement appliquée lors du paiement.
              </small>
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        {/* Payment Status */}
        <Col md={7}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Statut des Paiements par Tranche</h5>
              {/* <Button 
                                variant="success" 
                                size="sm"
                                onClick={() => handleQuickPayment()}
                                disabled={totals.remaining <= 0}
                            >
                                💳 Paiement Rapide
                            </Button> */}
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Tranche</th>
                    <th>Montant Requis</th>
                    <th>Montant Payé</th>
                    <th>Reste</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentStatus.map((status, index) => (
                    <tr
                      key={index}
                      className={
                        status.is_physical_only
                          ? "table-info"
                          : status.is_optional
                          ? "table-secondary"
                          : ""
                      }
                    >
                      <td>
                        {status.tranche.name}
                        {status.is_physical_only && (
                          <small className="text-info d-block">
                            <strong>(Paiement physique uniquement)</strong>
                          </small>
                        )}
                        {status.is_optional && !status.is_physical_only && (
                          <small className="text-muted d-block">
                            (Optionnelle)
                          </small>
                        )}
                      </td>
                      <td>
                        {status.is_physical_only ? (
                          <span className="text-info">Physical</span>
                        ) : (
                          <>
                            {formatAmount(status.required_amount)}
                            {status.has_global_discount &&
                              status.global_discount_amount > 0 && (
                                <div className="text-success">
                                  <small>
                                    {status.discount_percentage > 0
                                      ? `Avec réduction (${status.discount_percentage}%) : `
                                      : "Réduction appliquée : "}{" "}
                                    {formatAmount(
                                      parseFloat(status.required_amount) -
                                        parseFloat(
                                          status.global_discount_amount
                                        )
                                    )}
                                  </small>
                                </div>
                              )}
                          </>
                        )}
                      </td>
                      <td>
                        {status.is_physical_only ? (
                          <span className="text-info">Physical</span>
                        ) : (
                          <>
                            {formatAmount(status.paid_amount)}
                            {status.has_scholarship &&
                              status.scholarship_amount > 0 && (
                                <div className="text-success">
                                  <small>
                                    + Bourse:{" "}
                                    {formatAmount(status.scholarship_amount)}
                                    <br />={" "}
                                    {formatAmount(
                                      parseFloat(status.paid_amount) +
                                        parseFloat(status.scholarship_amount)
                                    )}
                                  </small>
                                </div>
                              )}
                            {status.has_global_discount &&
                              status.global_discount_amount > 0 && (
                                <div className="text-success">
                                  <small>
                                    + Réduction:{" "}
                                    {formatAmount(
                                      status.global_discount_amount
                                    )}{" "}
                                    {status.discount_percentage > 0 && (
                                      <>({status.discount_percentage}%)</>
                                    )}
                                    <br />={" "}
                                    {formatAmount(
                                      parseFloat(status.paid_amount) +
                                        parseFloat(
                                          status.global_discount_amount
                                        )
                                    )}
                                  </small>
                                </div>
                              )}
                          </>
                        )}
                      </td>
                      <td>
                        {status.is_physical_only ? (
                          <span className="text-info">Physical</span>
                        ) : (
                          <>
                            {status.has_scholarship &&
                            status.scholarship_amount > 0 ? (
                              <>
                                {formatAmount(
                                  Math.max(
                                    0,
                                    parseFloat(status.remaining_amount) 
                                  )
                                )}
                                {parseFloat(status.remaining_amount) -
                                  parseFloat(status.scholarship_amount) >
                                  0 && (
                                  <div className="text-success">
                                    <small>
                                      (Avec bourse:{" "}
                                      {formatAmount(
                                        Math.max(
                                          0,
                                          parseFloat(status.remaining_amount) 
                                            // parseFloat(
                                            //   status.scholarship_amount
                                            // )
                                        )
                                      )}
                                      )
                                    </small>
                                  </div>
                                )}
                              </>
                            ) : status.has_global_discount &&
                              status.global_discount_amount > 0 ? (
                              <>
                                {/* CORRECTION: remaining_amount inclut déjà toutes les réductions calculées par le backend */}
                                {formatAmount(Math.max(0, parseFloat(status.remaining_amount)))}
                                {parseFloat(status.remaining_amount) > 0 && (
                                  <div className="text-success">
                                    <small>
                                      (Avec réduction de {formatAmount(status.global_discount_amount)} appliquée)
                                    </small>
                                  </div>
                                )}
                              </>
                            ) : (
                              formatAmount(status.remaining_amount)
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {status.is_physical_only ? (
                          <>
                            <Badge
                              bg={status.rame_paid ? "success" : "warning"}
                            >
                              {status.rame_paid ? "Payé" : "Non payé"}
                            </Badge>
                            {!status.rame_paid && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="ms-2"
                                onClick={() => handlePayRame()}
                              >
                                Payer RAME
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Badge
                              bg={status.is_fully_paid ? "success" : "warning"}
                            >
                              {status.is_fully_paid
                                ? status.has_scholarship &&
                                  status.scholarship_amount > 0
                                  ? "Complet avec bourse"
                                  : "Complet"
                                : "Partiel"}
                            </Badge>
                            {status.is_optional && !status.is_fully_paid && (
                              <small className="text-muted d-block">
                                Non obligatoire
                              </small>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Payment History */}
        <Col md={5}>
          {/* Section RAME en haut de la colonne de droite */}
          <div className="mb-3">
            <RameStatusToggle
              studentId={studentId}
              studentName={
                student
                  ? `${student.first_name} ${student.last_name}`
                  : "Étudiant"
              }
              onStatusChange={(newStatus) => {
                console.log("Statut RAME mis à jour:", newStatus);
              }}
            />
          </div>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Historique des Paiements</h5>
              {(user?.role === 'comptable_superieur' || user?.role === 'accountant') && paymentHistory.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleDeleteHistory}
                  title="Supprimer tout l'historique"
                >
                  <Trash size={16} className="me-1" />
                  Supprimer l'historique
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {paymentHistory.length === 0 ? (
                <p className="text-muted text-center">
                  Aucun paiement enregistré
                </p>
              ) : (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {paymentHistory.map((payment) => (
                    <div key={payment.id} className="border-bottom pb-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong>{formatAmount(payment.total_amount)}</strong>
                          {payment.has_scholarship && (
                            <div className="text-success mt-1">
                              <small>
                                Montant payé:{" "}
                                {formatAmount(payment.total_amount)} + Bourse
                                appliquée:{" "}
                                {formatAmount(payment.scholarship_amount)} =
                                Valeur totale:{" "}
                                {formatAmount(
                                  payment.total_amount +
                                    payment.scholarship_amount
                                )}
                              </small>
                            </div>
                          )}
                          {payment.has_reduction &&
                            payment.reduction_amount > 0 && (
                              <div className="text-info mt-1">
                                <small>
                                  Montant payé:{" "}
                                  {formatAmount(payment.total_amount)} +
                                  Réduction appliquée:{" "}
                                  {formatAmount(payment.reduction_amount)} =
                                  Valeur totale:{" "}
                                  {formatAmount(
                                    payment.total_amount +
                                      payment.reduction_amount
                                  )}
                                </small>
                              </div>
                            )}
                          {(payment.has_scholarship ||
                            payment.has_reduction) && (
                            <span
                              className={`badge ms-2 ${
                                payment.has_scholarship
                                  ? "bg-success"
                                  : "bg-info"
                              }`}
                            >
                              {payment.has_scholarship && payment.has_reduction
                                ? "Bourse + Réduction"
                                : payment.has_scholarship
                                ? "Avec Bourse"
                                : "Avec Réduction"}
                            </span>
                          )}
                          <br />
                          <div className="text-muted d-flex align-items-center gap-3">
                            <small className="text-muted d-flex align-items-center mt-2">
                              <Calendar size={14} className="me-1" />
                              {formatDate(payment.payment_date)}
                            </small>
                            <br />
                            <small className="text-muted d-flex align-items-center">
                              <CreditCard size={14} className="me-1" />
                              {getPaymentMethodLabel(
                                payment.payment_method,
                                payment.is_rame_physical
                              )}
                            </small>
                          </div>

                          {/* Affichage du statut et des réductions appliquées */}
                          {(payment.status === 'cancelled' || payment.has_scholarship ||
                            payment.has_reduction) && (
                            <div className="mt-2">
                              {payment.status === 'cancelled' && (
                                <div className="badge bg-danger me-1 mb-1">
                                  ANNULÉ
                                </div>
                              )}
                              {payment.has_scholarship && (
                                <div className="badge bg-success me-1 mb-1">
                                  Bourse:{" "}
                                  {formatAmount(payment.scholarship_amount)}
                                </div>
                              )}
                              {payment.has_reduction &&
                                payment.reduction_amount > 0 && (
                                  <div className="badge bg-info me-1 mb-1">
                                    Réduction:{" "}
                                    {formatAmount(payment.reduction_amount)}
                                  </div>
                                )}
                              {payment.discount_reason && (
                                <small className="text-success d-block">
                                  <em>{payment.discount_reason}</em>
                                </small>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-success"
                            size="sm"
                            disabled={isPrintingReceipt}
                            onClick={() =>
                              handleDownloadPDFFromHistory(payment.id)
                            }
                            title={isPrintingReceipt ? "Téléchargement en cours..." : "Télécharger le PDF"}
                          >
                            {isPrintingReceipt ? (
                              <Spinner size="sm" />
                            ) : (
                              <>📄</>
                            )}
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            disabled={isPrintingReceipt}
                            onClick={() =>
                              handlePrintReceiptFromHistory(payment.id)
                            }
                            title={isPrintingReceipt ? "Impression en cours..." : "Imprimer le reçu"}
                          >
                            {isPrintingReceipt ? (
                              <Spinner size="sm" />
                            ) : (
                              <Receipt size={14} />
                            )}
                          </Button>

                          {/* Boutons Modifier/Supprimer pour comptables et admin */}
                          {((user?.role === 'comptable_superieur' || user?.role === 'accountant') ||
                            (user?.role === 'admin' && payment.status !== 'cancelled')) && (
                            <>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleEditPayment(payment)}
                                title="Modifier le paiement"
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeletePayment(payment)}
                                title="Supprimer le paiement"
                                disabled={payment.status === 'cancelled' && (user?.role !== 'comptable_superieur' && user?.role !== 'accountant')}
                              >
                                <Trash size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {payment.notes && (
                        <small className="text-muted d-block mt-1">
                          Note: {payment.notes}
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Nouveau Paiement</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePaymentSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Montant du versement ({schoolSettings.currency || "FCFA"}) *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max={totals.remaining}
                    step="1"
                    value={paymentForm.amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="Ex: 25000"
                    required
                  />
                  <Form.Text className="text-muted">
                    Reste à payer: {formatAmount(totals.remaining)}
                    <br />
                    {paymentForm.payment_method === "rame_physical" ? (
                      <small className="text-info">
                        ℹ️ Paiement par rame physique - Montant équivalent de la
                        tranche RAME
                      </small>
                    ) : (
                      <small className="text-warning">
                        ⚠️ Le montant ne peut pas dépasser le solde restant
                      </small>
                    )}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mode de paiement *</Form.Label>
                  <Form.Select
                    value={paymentForm.payment_method}
                    onChange={(e) => {
                      const newMethod = e.target.value;
                      setPaymentForm({
                        ...paymentForm,
                        payment_method: newMethod,
                        is_rame_physical: false, // Reset RAME choice when method changes
                      });
                    }}
                    required
                  >
                    <option value="cash">Banque</option>
                    <option value="card">Carte bancaire</option>
                    <option value="transfer">Virement</option>
                    <option value="check">Chèque</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Anciennes sections de réduction supprimées - maintenant intégrées dans la logique de date */}

            {/* Champ de saisie MANUELLE pour réduction nouveaux élèves (T2/T3) - Si pas encore appliquée */}
            {student?.student_status === 'new' && student?.arrival_trimester && !newcomerDiscountAlreadyApplied && (
              <Row className="mb-3">
                <Col>
                  <Alert variant="info" className="mb-2">
                    <strong>💡 Réduction pour nouvel élève (Trimestre {student.arrival_trimester})</strong>
                    <div className="mt-1">
                      <small>
                        Motif : <em>{student.newcomer_discount_reason || "Non spécifié"}</em>
                      </small>
                    </div>
                  </Alert>
                  <Form.Group>
                    <Form.Label>
                      Montant de réduction à appliquer ({schoolSettings.currency || "FCFA"})
                      <span className="text-danger"> *</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max={totals.remaining}
                      step="1"
                      value={paymentForm.newcomer_discount_amount}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          newcomer_discount_amount: e.target.value,
                        })
                      }
                      placeholder="Ex: 50000"
                      required
                    />
                    <Form.Text className="text-muted">
                      ⚠️ Saisissez le montant TOTAL de réduction à appliquer sur la scolarité (pas de calcul automatique)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* Message si réduction déjà appliquée */}
            {student?.student_status === 'new' && student?.arrival_trimester && newcomerDiscountAlreadyApplied && (
              <Row className="mb-3">
                <Col>
                  <Alert variant="success" className="mb-2">
                    <strong>✅ Réduction déjà appliquée</strong>
                    <div className="mt-1">
                      <small>
                        Une réduction de <strong>{formatAmount(appliedNewcomerDiscountAmount)} FCFA</strong> a déjà été appliquée pour cet élève.
                        Vous ne pouvez pas appliquer une nouvelle réduction newcomer.
                      </small>
                    </div>
                  </Alert>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de versement *</Form.Label>
                  <Form.Control
                    type="date"
                    value={paymentForm.versement_date}
                    onChange={(e) => {
                      setPaymentForm({
                        ...paymentForm,
                        versement_date: e.target.value,
                      });
                      // Vérifier l'éligibilité aux réductions quand la date change
                      checkDiscountEligibilityInModal(e.target.value);
                    }}
                    required
                  />
                  <Form.Text className="text-muted">
                    Date effective du versement de l'étudiant
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Date de validation{" "}
                    <small className="text-muted">(automatique)</small>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        payment_date: e.target.value,
                      })
                    }
                  />
                  <Form.Text className="text-muted">
                    Date officielle d'enregistrement du paiement dans le
                    système.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Section de réduction dynamique basée sur la date de versement */}
            {isCheckingDiscount && (
              <Row className="mb-3">
                <Col>
                  <div className="text-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <small className="text-muted">
                      Vérification des réductions disponibles...
                    </small>
                  </div>
                </Col>
              </Row>
            )}

            {modalDiscountInfo &&
              !modalDiscountInfo.not_eligible &&
              !modalDiscountInfo.is_date_expired && (
                <Row className="mb-3">
                  <Col>
                    <Card className="bg-success bg-opacity-10 border-success">
                      <Card.Body className="py-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="text-success mb-1">
                              🎉 Réduction de{" "}
                              {modalDiscountInfo.discount_percentage}%
                              disponible !
                            </h6>
                            <small className="text-muted">
                              Économisez{" "}
                              {formatAmount(
                                modalDiscountInfo.normal_totals.total_discount
                              )}
                              en payant{" "}
                              {formatAmount(
                                modalDiscountInfo.normal_totals
                                  .total_with_discount
                              )}
                              au lieu de{" "}
                              {formatAmount(
                                modalDiscountInfo.normal_totals.total_required
                              )}
                            </small>
                          </div>
                          <Form.Check
                            type="checkbox"
                            label="Appliquer la réduction"
                            checked={paymentForm.apply_discount}
                            onChange={(e) =>
                              handleDiscountToggle(e.target.checked)
                            }
                            className="text-success"
                          />
                        </div>
                        {paymentForm.apply_discount && (
                          <div className="mt-2 p-2 bg-success bg-opacity-25 rounded">
                            <small className="text-success">
                              ✅ <strong>Réduction activée :</strong> Le montant
                              sera automatiquement réparti sur toutes les
                              tranches avec les montants réduits.
                            </small>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

            {modalDiscountInfo && modalDiscountInfo.is_date_expired && (
              <Row className="mb-3">
                <Col>
                  <Card className="bg-warning bg-opacity-10 border-warning">
                    <Card.Body className="py-3">
                      <h6 className="text-warning mb-1">
                        ⏰ {modalDiscountInfo.deadline_message}
                      </h6>
                      <small className="text-muted">
                        La réduction de {modalDiscountInfo.discount_percentage}%
                        n'est plus disponible pour cette date de versement.
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {modalDiscountInfo && modalDiscountInfo.not_eligible && (
              <Row className="mb-3">
                <Col>
                  <Card className="bg-secondary bg-opacity-10 border-secondary">
                    <Card.Body className="py-3">
                      <h6 className="text-muted mb-1">
                        ℹ️ Réduction non disponible
                      </h6>
                      <small className="text-muted">
                        {modalDiscountInfo.message}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Numéro de référence</Form.Label>
                  <Form.Control
                    type="text"
                    value={paymentForm.reference_number}
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        reference_number: e.target.value,
                      })
                    }
                    placeholder="Numéro chèque, virement..."
                  />
                </Form.Group>
              </Col>
              <Col md={6}>{/* Empty column for spacing */}</Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={paymentForm.notes}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, notes: e.target.value })
                    }
                    placeholder="Notes additionnelles..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleModalClose}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" disabled={paymentLoading}>
              {paymentLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Check size={16} className="me-2" />
                  Enregistrer le Paiement
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Receipt Modal */}
      <Modal
        show={showReceiptModal}
        onHide={() => setShowReceiptModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reçu de Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div dangerouslySetInnerHTML={{ __html: receiptHtml }} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowReceiptModal(false)}
          >
            Fermer
          </Button>
          <Button 
            variant="success" 
            onClick={downloadReceiptPDF}
            disabled={isPrintingReceipt}
            className="me-2"
          >
            {isPrintingReceipt ? (
              <>
                <Spinner size="sm" className="me-2" />
                Téléchargement...
              </>
            ) : (
              <>
                📄 PDF
              </>
            )}
          </Button>
          {/* <Button 
            variant="primary" 
            onClick={printReceipt}
            disabled={isPrintingReceipt}
          >
            {isPrintingReceipt ? (
              <>
                <Spinner size="sm" className="me-2" />
                Ouverture...
              </>
            ) : (
              <>
                <Printer size={16} className="me-2" />
                Imprimer
              </>
            )}
          </Button> */}
        </Modal.Footer>
      </Modal>

      {/* Modal pour modifier un paiement (comptable_superieur uniquement) */}
      <Modal show={showEditPaymentModal} onHide={() => setShowEditPaymentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Modifier le Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingPayment && (
            <>
              <Alert variant="info">
                <strong>Paiement Original :</strong><br />
                Reçu N° : {editingPayment.receipt_number}<br />
                Étudiant : {student?.full_name}<br />
                Date création : {formatDate(editingPayment.created_at)}
              </Alert>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Montant Total (FCFA) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={editForm.total_amount}
                      onChange={(e) => setEditForm({...editForm, total_amount: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date de Paiement *</Form.Label>
                    <Form.Control
                      type="date"
                      value={editForm.payment_date}
                      onChange={(e) => setEditForm({...editForm, payment_date: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Méthode de Paiement *</Form.Label>
                    <Form.Select
                      value={editForm.payment_method}
                      onChange={(e) => setEditForm({...editForm, payment_method: e.target.value})}
                      required
                    >
                      <option value="cash">Banque</option>
                      <option value="card">Carte</option>
                      <option value="transfer">Virement</option>
                      <option value="check">Chèque</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Référence</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.reference_number}
                      onChange={(e) => setEditForm({...editForm, reference_number: e.target.value})}
                      placeholder="N° chèque, virement, etc."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Notes sur la modification..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditPaymentModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleSaveEditPayment}>
            Sauvegarder les modifications
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StudentPayment;
